import React, { useMemo, useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons';
// Barril do módulo — `hooks/index.ts` já reexporta os três (mesma forma do
// `FunnelAlertsTab.tsx:6`, que importa de `../hooks`).
import { useStagesByPipelineId, useUpdatePipeline, useUpdateStage } from '../hooks';
import type { TicketAlertConfig, TicketPipelineResponse, TicketStageResponse } from '@troia-v3/types';
import {
  TICKET_ALERT_DEFAULTS,
  buildSummaryText,
  clockToForm,
  hasStageOverride,
  validateClock,
  validateClockHours,
  type ClockFormState,
  type StageClockDraft,
} from './pipelineAlertHelpers';

/**
 * Aba "Alertas" do drawer de configuração de pipelines de chamados.
 *
 * Configura UM relógio — "Tempo na etapa" — em HORAS, com override opcional por
 * etapa. É o limiar contra o qual o card 07 "Relógio de etapa" pinta o trilho.
 *
 * MEDE ESTAGNAÇÃO, NÃO CONTRATO: nada aqui tem relação com `sla-policies`,
 * `SlaClock` ou os espelhos `slaBreached`/`slaDueAt`. Ver o cabeçalho do plano
 * DOCS/superpowers/plans/2026-07-28-sla-c-superficies-helpdesk.md.
 *
 * Transposição estrutural de `modules/funnels/components/FunnelAlertsTab.tsx`,
 * com três diferenças deliberadas: um relógio em vez de dois, horas em vez de
 * dias, e sem a seção de Notificações (`TicketAlertConfig` não tem esse campo).
 */
export interface PipelineAlertsTabProps {
  pipeline: TicketPipelineResponse;
}

export const PipelineAlertsTab: React.FC<PipelineAlertsTabProps> = ({ pipeline }) => {
  const { data: stagesData, isLoading: stagesLoading } = useStagesByPipelineId(pipeline.id);
  const updatePipeline = useUpdatePipeline();
  const updateStage = useUpdateStage();

  const stages = useMemo(() => stagesData ?? [], [stagesData]);

  const [timeInStage, setTimeInStage] = useState<ClockFormState>(() =>
    clockToForm(pipeline.alertConfig?.timeInStage, TICKET_ALERT_DEFAULTS.timeInStage),
  );

  const [expandedStageId, setExpandedStageId] = useState<string | null>(null);
  const [stageDraft, setStageDraft] = useState<StageClockDraft | null>(null);
  const [stageDraftError, setStageDraftError] = useState<string | null>(null);

  const timeInStageError = validateClock(timeInStage);
  const canSavePipelineAlerts = !timeInStageError;

  const summaryText = useMemo(() => buildSummaryText(timeInStage), [timeInStage]);

  const effectivePipelineClock =
    pipeline.alertConfig?.timeInStage ?? TICKET_ALERT_DEFAULTS.timeInStage;

  const handleSavePipelineAlerts = async () => {
    if (!canSavePipelineAlerts) return;
    const alertConfig: TicketAlertConfig = {
      timeInStage: {
        enabled: timeInStage.enabled,
        warningHours: timeInStage.warningHours,
        criticalHours: timeInStage.criticalHours,
      },
    };
    try {
      await updatePipeline.mutateAsync({ id: pipeline.id, data: { alertConfig } });
    } catch {
      // toast já tratado no hook useUpdatePipeline
    }
  };

  const handleTogglePersonalize = (stage: TicketStageResponse) => {
    if (expandedStageId === stage.id) {
      setExpandedStageId(null);
      setStageDraft(null);
      setStageDraftError(null);
      return;
    }
    const override = stage.alertConfig?.timeInStage;
    // O rascunho nasce com o valor EFETIVO (override, senão o do pipeline, senão
    // o do sistema) — abrir o painel com campos vazios faria o usuário reescrever
    // do zero um valor que já vale.
    setStageDraft({
      warningHours: override?.warningHours ?? effectivePipelineClock.warningHours,
      criticalHours: override?.criticalHours ?? effectivePipelineClock.criticalHours,
    });
    setStageDraftError(null);
    setExpandedStageId(stage.id);
  };

  const handleSaveStageOverride = async (stage: TicketStageResponse) => {
    if (!stageDraft) return;
    const err = validateClockHours(stageDraft.warningHours, stageDraft.criticalHours);
    if (err) {
      setStageDraftError(err);
      return;
    }
    const alertConfig: TicketAlertConfig = {
      timeInStage: {
        enabled: true,
        warningHours: stageDraft.warningHours,
        criticalHours: stageDraft.criticalHours,
      },
    };
    try {
      await updateStage.mutateAsync({ id: stage.id, data: { alertConfig } });
      setExpandedStageId(null);
      setStageDraft(null);
      setStageDraftError(null);
    } catch {
      // toast já tratado no hook useUpdateStage
    }
  };

  const handleRestoreStageDefault = async (stage: TicketStageResponse) => {
    try {
      // Objeto vazio → clock ausente → herda o pipeline (Zod da C3 aceita `{}`).
      await updateStage.mutateAsync({ id: stage.id, data: { alertConfig: {} } });
    } catch {
      // toast já tratado no hook useUpdateStage
    } finally {
      if (expandedStageId === stage.id) {
        setExpandedStageId(null);
        setStageDraft(null);
        setStageDraftError(null);
      }
    }
  };

  return (
    <div className="space-y-0" data-testid="pipeline-alerts-tab">
      <div className="pb-5">
        <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
          Avisos visuais no kanban quando um chamado fica parado na mesma etapa. O relógio mede
          estagnação — tempo desde a entrada na etapa — e não tem relação com prazo de SLA.
        </p>

        <div
          className="flex items-start gap-3 p-3.5 rounded-xl border mt-4"
          style={{ background: 'linear-gradient(180deg,#fbfaff,#f5f2ff)', borderColor: '#eae6fb' }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#ffffff', color: '#7c3aed', boxShadow: 'inset 0 0 0 1px rgba(139,92,246,0.10)' }}
          >
            <Icon name="clock" size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>Relógio ativo</div>
            <div className="text-xs mt-0.5" style={{ color: '#64748b' }} data-testid="pipeline-alerts-summary">
              {summaryText}
            </div>
          </div>
        </div>
      </div>

      <AlertClockSection
        title="Tempo na etapa"
        description="Horas desde que o chamado entrou na etapa atual."
        state={timeInStage}
        onChange={setTimeInStage}
        error={timeInStageError}
        testidPrefix="pipeline-alerts-time-in-stage"
      />

      <div className="py-5 border-t" style={{ borderColor: '#f1f5f9' }}>
        <label className="block text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: '#94a3b8' }}>
          Limites por etapa
        </label>

        {stagesLoading ? (
          <div className="text-center py-6 text-sm" style={{ color: '#64748b' }}>
            Carregando etapas...
          </div>
        ) : stages.length === 0 ? (
          <div className="text-center py-6 text-sm rounded-lg border border-dashed" style={{ color: '#9ca3af', borderColor: '#e5e7eb' }}>
            Adicione etapas para configurar limites específicos por etapa.
          </div>
        ) : (
          <div className="flex flex-col">
            {stages.map((stage) => (
              <StageAlertRow
                key={stage.id}
                stage={stage}
                isExpanded={expandedStageId === stage.id}
                draft={expandedStageId === stage.id ? stageDraft : null}
                error={expandedStageId === stage.id ? stageDraftError : null}
                onTogglePersonalize={() => handleTogglePersonalize(stage)}
                onChangeDraft={setStageDraft}
                onSave={() => handleSaveStageOverride(stage)}
                onRestoreDefault={() => handleRestoreStageDefault(stage)}
                isSaving={updateStage.isPending}
              />
            ))}
          </div>
        )}
      </div>

      <div className="pt-5 flex items-center justify-end gap-2">
        <Button
          variant="primary"
          onClick={handleSavePipelineAlerts}
          disabled={!canSavePipelineAlerts}
          loading={updatePipeline.isPending}
          data-testid="pipeline-alerts-save"
        >
          Salvar configurações de alerta
        </Button>
      </div>
    </div>
  );
};

PipelineAlertsTab.displayName = 'PipelineAlertsTab';

// ============================================================================
// AlertClockSection — bloco do relógio "Tempo na etapa"
// ============================================================================

interface AlertClockSectionProps {
  title: string;
  description: string;
  state: ClockFormState;
  onChange: (next: ClockFormState) => void;
  error: string | null;
  testidPrefix: string;
}

const AlertClockSection: React.FC<AlertClockSectionProps> = ({
  title,
  description,
  state,
  onChange,
  error,
  testidPrefix,
}) => (
  <div className="py-5 border-t" style={{ borderColor: '#f1f5f9' }}>
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: '#1e293b' }}>{title}</div>
        <div className="text-xs mt-1" style={{ color: '#64748b' }}>{description}</div>
      </div>
      <div data-testid={`${testidPrefix}-toggle`}>
        <Switch checked={state.enabled} onChange={(checked) => onChange({ ...state, enabled: checked })} />
      </div>
    </div>

    {state.enabled && (
      <>
        <div className="flex flex-wrap gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
            Alerta após
            <Input
              type="number"
              min={1}
              value={state.warningHours}
              onChange={(e) => onChange({ ...state, warningHours: Number(e.target.value) })}
              fullWidth={false}
              style={{ width: 68, textAlign: 'center' }}
              data-testid={`${testidPrefix}-warning-hours`}
            />
            horas
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
            Crítico após
            <Input
              type="number"
              min={1}
              value={state.criticalHours}
              onChange={(e) => onChange({ ...state, criticalHours: Number(e.target.value) })}
              fullWidth={false}
              style={{ width: 68, textAlign: 'center' }}
              data-testid={`${testidPrefix}-critical-hours`}
            />
            horas
          </label>
        </div>

        {error && (
          <p className="text-xs mt-2" style={{ color: '#dc2626' }} data-testid={`${testidPrefix}-error`}>
            {error}
          </p>
        )}
        <p className="text-xs mt-2" style={{ color: '#94a3b8' }}>
          Conta tempo de parede desde a entrada na etapa, sem horário comercial.
        </p>
      </>
    )}
  </div>
);

// ============================================================================
// StageAlertRow — linha de etapa em "Limites por etapa"
// ============================================================================

interface StageAlertRowProps {
  stage: TicketStageResponse;
  isExpanded: boolean;
  draft: StageClockDraft | null;
  error: string | null;
  onTogglePersonalize: () => void;
  onChangeDraft: (draft: StageClockDraft) => void;
  onSave: () => void;
  onRestoreDefault: () => void;
  isSaving: boolean;
}

const StageAlertRow: React.FC<StageAlertRowProps> = ({
  stage,
  isExpanded,
  draft,
  error,
  onTogglePersonalize,
  onChangeDraft,
  onSave,
  onRestoreDefault,
  isSaving,
}) => {
  const overridden = hasStageOverride(stage);
  const btnLabel = isExpanded ? 'Recolher' : overridden ? 'Editar personalização' : 'Personalizar';

  return (
    <div
      className="py-3 border-t first:border-t-0"
      style={{ borderColor: '#f1f5f9' }}
      data-testid={`pipeline-alerts-stage-${stage.id}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
        <span className="text-sm font-medium flex-1 min-w-0 truncate" style={{ color: '#1e293b' }}>
          {stage.name}
        </span>
        <span
          className="text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 whitespace-nowrap"
          style={
            overridden
              ? { backgroundColor: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' }
              : { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }
          }
          data-testid={`pipeline-alerts-stage-pill-${stage.id}`}
        >
          {overridden ? 'Personalizado' : 'Padrão do pipeline'}
        </span>
        <button
          type="button"
          onClick={onTogglePersonalize}
          className="flex items-center gap-1 text-xs font-semibold flex-shrink-0 whitespace-nowrap"
          style={{ color: '#7c3aed' }}
          data-testid={`pipeline-alerts-stage-personalize-${stage.id}`}
        >
          <span>{btnLabel}</span>
          <span className="inline-flex transition-transform" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}>
            <Icon name="chevron-down" size={14} />
          </span>
        </button>
      </div>

      {isExpanded && draft && (
        <div className="mt-3 p-4 rounded-xl" style={{ backgroundColor: '#fafafa', border: '1px solid #e8e8ec' }}>
          <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: '#94a3b8' }}>
            Tempo nesta etapa
          </label>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Alerta após
              <Input
                type="number"
                min={1}
                value={draft.warningHours}
                onChange={(e) => onChangeDraft({ ...draft, warningHours: Number(e.target.value) })}
                fullWidth={false}
                style={{ width: 68, textAlign: 'center' }}
                data-testid={`pipeline-alerts-stage-warning-${stage.id}`}
              />
              horas
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Crítico após
              <Input
                type="number"
                min={1}
                value={draft.criticalHours}
                onChange={(e) => onChangeDraft({ ...draft, criticalHours: Number(e.target.value) })}
                fullWidth={false}
                style={{ width: 68, textAlign: 'center' }}
                data-testid={`pipeline-alerts-stage-critical-${stage.id}`}
              />
              horas
            </label>
          </div>
          {error && <p className="text-xs mt-2" style={{ color: '#dc2626' }}>{error}</p>}

          <div className="flex items-center gap-4 mt-4">
            <Button
              variant="primary"
              onClick={onSave}
              loading={isSaving}
              data-testid={`pipeline-alerts-stage-save-${stage.id}`}
            >
              Salvar personalização
            </Button>
            {overridden && (
              <button
                type="button"
                onClick={onRestoreDefault}
                className="text-xs font-semibold"
                style={{ color: '#dc2626' }}
                data-testid={`pipeline-alerts-stage-restore-${stage.id}`}
              >
                Restaurar padrão
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

StageAlertRow.displayName = 'StageAlertRow';
